import { getFormValues } from 'redux-form';
import { createSelector } from 'reselect';

import { RefusalsToolbarFormValues } from '../../../routes/Refusals/RefusalsPage/RefusalsToolbar/RefusalsToolbar';
import { Forms } from '../../form/form.selector';
import { RootState } from '../../reducers';
import { DateRangeDefined } from '../dataTools/dataTools.types';
import { getAllGroups } from '../group/group.selector';
import { findRecipeById, getRecipes } from '../recipes/recipes.selector';
import {
  DeliveryItemWithRation,
  DeliverySummaryItem,
  RefusalExtended,
  RefusalsBatch,
  RefusalsDataItem,
} from './refusals.types';
import { getPremixes } from '../premixes/premixes.selector';
import { getRefusalsPreset } from '../preset/preset.selector';

const getBatches = (state: RootState): RefusalsBatch[] => state.entities.refusals.batches;

export const getSelectedTimeRange = createSelector(
  [getFormValues(Forms.refusalsToolbarForm)],
  (formValues: RefusalsToolbarFormValues): DateRangeDefined | null => {
    const selectedTimeRange = formValues ? formValues.selectedTimeRange : undefined;
    if (!selectedTimeRange) {
      return null;
    }

    return {
      startDate: selectedTimeRange.startDate!,
      endDate: selectedTimeRange.endDate!,
    };
  },
);

export const getRefusalsDeliveryData = createSelector(
  [getBatches, getRecipes, getPremixes, getAllGroups, getRefusalsPreset],
  (batches, recipes, premixes, groupList, preset) => {
    const includedGroupIds: string[] | undefined = preset?.dataSettings?.includedGroups;

    let deliveries = batches.reduce<DeliveryItemWithRation[]>((acc, curr) => {
      return [
        ...acc,
        ...curr.deliveries.map((d) => ({
          ...d,
          rationId: curr.rationId,
          refusalWeight: d.refusalWeight || 0,
          batchId: curr.id,
        })),
      ];
    }, []);
    deliveries = includedGroupIds ? deliveries.filter(({ groupId }) => includedGroupIds.includes(groupId)) : deliveries;

    const groupedData = deliveries.reduce<RefusalsDataItem[]>((acc, curr) => {
      const { groupId, rationId, ...other } = curr;

      const ration = findRecipeById(recipes, premixes, rationId);
      const deliveryItem = {
        ...other,
        rationName: ration?.description || '',
        dryWeight: Number((other.actualWeight * (other.dryMatterPerc / 100)).toFixed(2)),
      };

      const index = acc.findIndex((x) => x.groupId === groupId);
      if (index === -1) {
        const summary: DeliverySummaryItem = {
          actualWeight: deliveryItem.actualWeight,
          dryWeight: deliveryItem.dryWeight,
          headCount: deliveryItem.headCount,
          refusalWeight: deliveryItem.refusalWeight,
        };
        const group = groupList.find((group) => group.id === groupId)!;
        const groupName = group ? group.description : '';
        return [...acc, { groupName, groupId, deliveryItems: [deliveryItem], summary }];
      } else {
        return acc
          .map((x, i) => {
            if (i !== index) {
              return x;
            } else {
              return {
                ...x,
                deliveryItems: [...x.deliveryItems, deliveryItem],
                summary: {
                  actualWeight: x.summary.actualWeight + deliveryItem.actualWeight,
                  dryWeight: x.summary.dryWeight + deliveryItem.dryWeight,
                  headCount: x.summary.headCount + deliveryItem.headCount,
                  refusalWeight: x.summary.refusalWeight + deliveryItem.refusalWeight,
                },
              };
            }
          })
          .sort((a, b) => a.groupName.localeCompare(b.groupName, undefined, { numeric: true }));
      }
    }, []);

    return groupedData.map((x) => {
      const deliveriesCount = x.deliveryItems.length;
      return {
        ...x,
        summary: {
          ...x.summary,
          headCount: x.summary.headCount / deliveriesCount,
        },
      };
    });
  },
);

export const getRefusalsDeliveryDataFilteredBySearch = createSelector(
  [getRefusalsDeliveryData, getFormValues(Forms.refusalsSearchForm)],
  (data: RefusalsDataItem[], formValue: any) => {
    if (!formValue) return data;
    const search = formValue.search.trim().toLowerCase();
    if (!search) return data;

    return data.filter((x) => x.groupName.toLowerCase().includes(search));
  },
);

export const getRefusals = (state: RootState) => state.entities.refusals.refusals;
export const getEntities = (state: RootState) => state.entities;

export const getRefusalsExtended = createSelector([getRefusals, getEntities], (refusals, entities) => {
  const { ingredients, group: groups } = entities;

  return refusals.map((x) => {
    const ingredient = x.ingredientId ? ingredients.ingredientList.find((ing) => ing.id === x.ingredientId) : undefined;
    const group = x.groupId ? groups.find((g) => g.id === x.groupId) : undefined;
    return {
      ...x,
      ingredientName: ingredient && ingredient.description,
      groupName: group && group.description,
    };
  });
});

export const getRefusalsExtendedFilteredBySearch = createSelector(
  [getRefusalsExtended, getFormValues(Forms.refusalsSearchForm)],
  (refusals: RefusalExtended[], formValue: any) => {
    if (!formValue) return refusals;
    const search = formValue.search.trim().toLowerCase();
    if (!search) return refusals;

    return refusals.filter(
      (x) =>
        (x.groupName && x.groupName.toLowerCase().includes(search)) ||
        (x.ingredientName && x.ingredientName.toLocaleLowerCase().includes(search)),
    );
  },
);

export const getRefusalsFilterSettings = (state: RootState) => state.entities.refusals.filterSettings;
export const getChangedRefusalIds = (state: RootState): string[] => state.entities.refusals.changedRefusalIds;
